import React, { useState } from "react";
import "./styles.scss";
import { useIsDesktopOrTablet } from "./../../../../../hooks";

const calculateSplitPercentage = (
  value: number,
  direction: "left" | "right",
  centerPoint: number = 50
): number => {
  if (direction === "left") {
    return ((centerPoint - value) / centerPoint) * 100;
  } else {
    return ((value - centerPoint) / centerPoint) * 100;
  }
};

const getImageScale = (sliderValue: number, side?: "left" | "right") => {
  if (!side) return 0.5 + (sliderValue / 100) * 0.5; // Default for standard mode

  if (side === "left" && sliderValue < 50) {
    return 0.5 + ((50 - sliderValue) / 50) * 0.5; // Scales from 1 at (0 slider value) to 0.5 at (50 max slider value)
  }
  if (side === "right" && sliderValue > 50) {
    return 0.5 + ((sliderValue - 50) / 50) * 0.5; // Scales from 0.5 at (50 slider value) to 1 at (100 max slider value)
  }
  return 0.5;
};

const TrackFill = ({
  className,
  maskDirection,
  fillValue,
  isActive = true,
}: {
  className: string;
  maskDirection: "left" | "right";
  fillValue: number;
  isActive?: boolean;
}) => (
  <div
    className={className}
    style={{
      background: "var(--fl-color-primary)",
      opacity: isActive ? 1 : 0,
      maskImage: `linear-gradient(to ${maskDirection}, white ${fillValue}%, transparent ${fillValue}%)`,
    }}
  ></div>
);

const ScalingImage = ({
  imageScale,
  imageSrc,
}: {
  imageScale: number;
  imageSrc: string;
}) => (
  <div
    className="scaling-image-container"
    style={{ transform: `scale(${imageScale})` }}
  >
    <img src={imageSrc} alt="Slider indicator" />
  </div>
);

export const SplitSlider = ({
  mode,
  imageSrc,
  rightImageSrc,
  leftLabel,
  rightLabel,
  initialValue,
  onValueChange,
}: {
  mode?: "standard" | "split";
  imageSrc?: string;
  rightImageSrc?: string;
  leftLabel?: string;
  rightLabel?: string;
  initialValue: number;
  onValueChange?: (value: number) => void;
}) => {
  const [sliderValue, setSliderValue] = useState(initialValue);
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const isDesktopOrTablet = useIsDesktopOrTablet();

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setSliderValue(newValue);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      onValueChange?.(newValue);
    }, 300);
  };

  const isSplitMode = mode === "split";

  const sliderPercent = `${sliderValue}%`;

  const slider = (
    <div className="slider">
      {isSplitMode ? (
        <>
          <div className="left-track"></div>
          <TrackFill
            className="left-track-fill"
            maskDirection="left"
            fillValue={
              sliderValue < 50
                ? calculateSplitPercentage(sliderValue, "left")
                : 0
            }
            isActive={sliderValue < 50}
          />
          <div className="right-track"></div>
          <TrackFill
            className="right-track-fill"
            maskDirection="right"
            fillValue={
              sliderValue > 50
                ? calculateSplitPercentage(sliderValue, "right")
                : 0
            }
            isActive={sliderValue > 50}
          />
        </>
      ) : (
        <>
          <div className="slider-track"></div>
          <TrackFill
            className="slider-fill"
            maskDirection="right"
            fillValue={sliderValue}
          />
        </>
      )}
      <div className="slider-thumb" style={{ left: sliderPercent }}></div>
      <input
        type="range"
        min="0"
        max="100"
        value={sliderValue}
        onChange={handleValueChange}
        className="slider-input"
      />
    </div>
  );

  return (
    <div className="tg_split-slider">
      <div className="slider-inner">
        <div className="image-section">
          {isSplitMode ? (
            <>
              {imageSrc && sliderValue < 50 && (
                <ScalingImage
                  imageScale={getImageScale(sliderValue, "left")}
                  imageSrc={imageSrc}
                />
              )}
              {rightImageSrc && sliderValue > 50 && (
                <ScalingImage
                  imageScale={getImageScale(sliderValue, "right")}
                  imageSrc={rightImageSrc}
                />
              )}
            </>
          ) : (
            imageSrc && (
              <ScalingImage
                imageScale={getImageScale(sliderValue)}
                imageSrc={imageSrc}
              />
            )
          )}
        </div>
        <div className="slider-section">
          {!isDesktopOrTablet && (
            <>
              <div className="labels">
                <span>{leftLabel}</span>
                <span>{rightLabel}</span>
              </div>
              {slider}
            </>
          )}
          {isDesktopOrTablet && (
            <div className="labels">
              <span>{leftLabel}</span>
              {slider}
              <span>{rightLabel}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
